import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConfigService } from '../Services/AppConfigService';

export interface SubAgentEntry {
  name: string;
  description: string;
}

export interface AgentCatalogEntry {
  name: string;
  layer: number;
  icon: string;
  color: string;
  role: string;
  description: string;
  responsibilities: string[];
  knowledgeDomains: string[];
  inputs: string;
  outputs: string;
  guidance: string[];
  subAgents: SubAgentEntry[];
}

@Injectable({ providedIn: 'root' })
export class AgentCatalogService {

  constructor(private http: HttpClient, private config: AppConfigService) {}

  private get baseUrl(): string {
    return this.config.apiBaseUrl + '/api/AgentCatalog';
  }

  getAll(): Observable<AgentCatalogEntry[]> {
    return this.http.get<AgentCatalogEntry[]>(this.baseUrl).pipe(
      catchError(() => of(FALLBACK_CATALOG))
    );
  }

  getByName(name: string): Observable<AgentCatalogEntry | null> {
    return this.http.get<AgentCatalogEntry>(`${this.baseUrl}/${name}`).pipe(
      catchError(() => of(null))
    );
  }
}

// Local fallback so the page works even when the API is offline
export const FALLBACK_CATALOG: AgentCatalogEntry[] = [
  {
    name: 'OrchestratorAgent', layer: 0, icon: 'hub', color: '#7C3AED',
    role: 'Pipeline Coordinator',
    description: 'Decomposes high-level goals into sub-tasks and routes each one to the correct specialist agent.',
    responsibilities: ['Parse goals via LLM decomposition', 'Route tasks to domain-specific agents', 'Aggregate and rank results by confidence'],
    knowledgeDomains: ['Goal decomposition', 'Agent routing', 'Pipeline error recovery'],
    inputs: 'Plain-language goal string',
    outputs: 'Ranked AgentResult collection + pipeline summary',
    guidance: ['Run: python main.py "your goal"', 'Results are ranked by confidence — the first is most reliable.'],
    subAgents: [],
  },
  {
    name: 'DatabaseAgent', layer: 1, icon: 'storage', color: '#D97706',
    role: 'Database Developer',
    description: 'Full database lifecycle: schema design, DDL, stored procedures, ORM entities, migrations, and security.',
    responsibilities: ['Design 3NF schemas', 'Generate DDL and stored procedures', 'Build EF Core entities', 'Apply schema to WebAPI via Claude Code'],
    knowledgeDomains: ['SQL Server / PostgreSQL', 'Entity Framework Core', 'Row-level security'],
    inputs: 'Goal / query + optional context',
    outputs: 'schema_design, query_library, data_model, Claude Code write results',
    guidance: ['Run EF Core migrations after DatabaseAgent completes.'],
    subAgents: [
      { name: 'SchemaDesignSubAgent', description: 'Designs ER model and produces DDL.' },
      { name: 'QueryBuilderSubAgent', description: 'Generates SQL query library.' },
      { name: 'DataModelSubAgent', description: 'Creates ORM entities and documentation.' },
    ],
  },
  {
    name: 'EngineeringAgent', layer: 1, icon: 'code', color: '#059669',
    role: 'Application Developer',
    description: 'Generates Angular + ASP.NET Core code bundles and writes them to real project directories via Claude Code.',
    responsibilities: ['Generate Angular components and services', 'Generate .NET controllers and models', 'Write files via Claude Code CLI'],
    knowledgeDomains: ['Angular 17+', 'ASP.NET Core 8', 'EF Core', 'Claude Code CLI'],
    inputs: 'Feature goal + research context',
    outputs: 'angular_files[], dotnet_files[], Claude Code write results',
    guidance: ['Review git diff after each run to see what Claude Code wrote.'],
    subAgents: [],
  },
  {
    name: 'MarketingAgent', layer: 1, icon: 'campaign', color: '#DC2626',
    role: 'Marketing Specialist',
    description: 'Produces complete marketing packages: strategy, copy, email, social posts, SVG assets, and Angular landing pages.',
    responsibilities: ['Generate campaign copy', 'Create SVG graphic assets', 'Write HTML email templates', 'Deploy Angular marketing pages via Claude Code'],
    knowledgeDomains: ['B2B SaaS marketing', 'GC buyer journey', 'SVG asset generation', 'SEO'],
    inputs: 'Campaign goal + research summary',
    outputs: 'Copy pack, HTML email, SVG assets, Angular landing page',
    guidance: ['Set HF_TOKEN to enable AI-generated PNG images.'],
    subAgents: [],
  },
  {
    name: 'GraphicDesignerAgent', layer: 1, icon: 'palette', color: '#EC4899',
    role: 'Graphic Designer',
    description: 'Produces a complete enterprise marketing design package using free services: brand identity kit, AI-generated images (HuggingFace), infographic, HTML pitch deck, IAB ad pack, and print-ready business card.',
    responsibilities: [
      'SVG brand identity kit: logo (dark+light), icon mark, color palette card, typography specimen',
      'AI-generated images via HuggingFace: FLUX.1-schnell, SDXL, DreamShaper, SD-v1.5',
      'SVG infographic (900×1200) with stats and process flow',
      '5-slide HTML/CSS pitch deck — print-ready, dark-mode branded',
      'IAB digital ad pack: Leaderboard, Med Rect, Skyscraper, Half Page, Billboard, Mobile Banner',
      'Print-ready SVG business card (3.5"×2" @300dpi, front and back)',
    ],
    knowledgeDomains: [
      'Brand identity design (logo, color systems, typography)',
      'AI image generation (HuggingFace Inference API)',
      'SVG vector graphics (pure Python, no dependencies)',
      'IAB digital advertising standards',
      'Print production standards',
      'HTML/CSS presentation design',
    ],
    inputs: 'Campaign goal + optional research context',
    outputs: 'Brand kit (5 SVG), AI images (5 PNG/SVG), infographic, slide deck, ad pack, business card, JSON manifest',
    guidance: [
      'Set HF_TOKEN env var to generate real AI PNG images via HuggingFace.',
      'Without HF_TOKEN all deliverables still generate — images use professional SVG placeholders.',
      'HTML slide deck → open in Chrome → Print → Save as PDF for a professional PDF deck.',
      'All output saved to workspace/reports/graphic_designer/ with a JSON manifest.',
    ],
    subAgents: [
      { name: 'BrandIdentitySubAgent',   description: 'Creates SVG brand kit: logo, icon mark, color palette card, and typography specimen.' },
      { name: 'ImageGenerationSubAgent', description: 'Generates 5 AI marketing images via HuggingFace. Falls back to SVG placeholders when offline.' },
      { name: 'LayoutDesignSubAgent',    description: 'Infographic, HTML pitch deck, IAB ad pack, and print business card — pure Python SVG + HTML/CSS.' },
    ],
  },
  {
    name: 'ResearchAgent', layer: 1, icon: 'search', color: '#0284C7',
    role: 'Market Researcher',
    description: 'Performs web searches, synthesises findings, and verifies sources for all downstream agents.',
    responsibilities: ['Execute multi-engine web searches', 'Summarise findings', 'Verify source credibility'],
    knowledgeDomains: ['Construction industry research', 'Competitive intelligence', 'Source verification'],
    inputs: 'Goal / query string',
    outputs: 'Structured research summary with TLDR, key_points, citations',
    guidance: ['Set SERPAPI_KEY or BING_KEY for richer results.'],
    subAgents: [
      { name: 'WebSearchSubAgent', description: 'Executes web searches.' },
      { name: 'SummariseSubAgent', description: 'Condenses results into structured summary.' },
      { name: 'SourceVerifySubAgent', description: 'Verifies citations and flags unconfirmed sources.' },
    ],
  },
];
